import React from "react";
import { useNavigate } from "react-router-dom";
import { defaultPfpUrl } from "../../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";
import {
  FormattedBody,
  FORMATTED_BODY_LINK_ON_ACCENT_CLASS,
  FORMATTED_BODY_MENTION_ON_ACCENT_CLASS,
} from "../../ui/formatted-body";
import { cn } from "../../../lib/utils";
import { parseShareMetadata } from "../../feed_page/shareUtils";

/**
 * Tappable in-thread preview of a shared post or event.
 * @param {Object} props
 * @param {object} props.message
 * @param {boolean} props.isOwnMessage
 * @returns {JSX.Element | null}
 */
function SharedContentCard({ message, isOwnMessage }) {
  const navigate = useNavigate();
  const meta = parseShareMetadata(message) || {};
  const contentType =
    meta.content_type === "event" || message.message_type === "event"
      ? "event"
      : "post";
  const contentId = meta.content_id;
  const commentId = meta.comment?.id;
  let path = meta.permalink;
  if (!path) {
    path = contentType === "event" ? `/event/${contentId}` : `/post/${contentId}`;
    if (commentId) path += `?comment=${commentId}`;
  }
  const caption = meta.caption || (contentType === "event" ? "Event" : "Post");
  const authorName = meta.author_name || "Author";
  const comment = meta.comment;

  const openSharedContent = (event) => {
    if (event.target.closest("a")) return;
    navigate(path);
  };

  return (
    <div className={cn("flex max-w-xs flex-col gap-1.5 lg:max-w-sm", isOwnMessage ? "items-end" : "items-start")}>
      {message.content ? (
        <FormattedBody
          className={cn(
            "max-w-full rounded-2xl px-4 py-2 text-sm shadow-sm",
            isOwnMessage
              ? "bg-message-sent text-text-on-accent"
              : "bg-message-received text-text-primary"
          )}
          mentions={message.mentions}
          linkClassName={
            isOwnMessage ? FORMATTED_BODY_LINK_ON_ACCENT_CLASS : undefined
          }
          mentionClassName={
            isOwnMessage ? FORMATTED_BODY_MENTION_ON_ACCENT_CLASS : undefined
          }
        >
          {message.content}
        </FormattedBody>
      ) : null}
      <div
        role="link"
        tabIndex={0}
        onClick={openSharedContent}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(path);
          }
        }}
        className={cn(
          "block w-64 cursor-pointer overflow-hidden rounded-2xl border border-ui-border bg-ui-card text-left shadow-sm outline-none ring-offset-2 ring-offset-primary transition hover:bg-ui-muted focus-visible:ring-2 focus-visible:ring-ui-ring"
        )}
      >
        {meta.image_url ? (
          <img
            src={meta.image_url}
            alt=""
            className="h-36 w-full object-cover"
          />
        ) : null}
        <div className="flex items-start gap-2.5 px-3 py-2.5">
          <Avatar className="h-8 w-8 shrink-0">
            <AvatarImage
              src={meta.author_pfp || defaultPfpUrl}
              alt=""
              onError={(event) => {
                event.target.src = defaultPfpUrl;
              }}
            />
            <AvatarFallback className="text-xs">
              {authorName.slice(0, 1).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-text-primary">{authorName}</div>
            <FormattedBody
              className="line-clamp-2 text-sm text-text-secondary"
              mentions={meta.mentions}
            >
              {caption}
            </FormattedBody>
          </div>
        </div>
        {comment ? (
          <div className="border-t border-ui-border bg-ui-muted px-3 py-2">
            <div className="mb-0.5 truncate text-xs font-medium text-text-secondary">
              {comment.author_name || "Comment"}
            </div>
            <FormattedBody
              className="line-clamp-3 text-sm text-text-primary"
              mentions={comment.mentions}
            >
              {comment.content}
            </FormattedBody>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default SharedContentCard;
